import { useCallback, useEffect, useRef } from 'react';
import type { ComboBoxPrediction, IsMulti, Location } from '~/utilities';
import { ActionMeta, StylesConfig, SelectInstance } from 'react-select';
import AsyncSelect from 'react-select/async';
import {
  defaultMapLocation,
  inputBaseStyles,
  calculateBoundingBox,
  logErrorToSentry,
  ErrorLevel,
} from '~/utilities';
import {
  NoOptionsMessage,
  LocationInputOption,
  UseCurrentLocationOption,
  Control,
} from './components';
import { useAutocompleteService } from '~/hooks';
import { useAppSelector, useAppDispatch } from '~/state/hooks';
import {
  selectHasLocation,
  selectLocation,
  selectCanUseGeolocation,
  setCanUseGeolocation,
  setLocation,
  setLocationIsWithinSupportedArea,
} from '~/state/location';
import {
  setMapBounds,
  setMapInteractionActive,
  setLocationManuallySelected,
} from '~/state/mapData';
import { clearSelectedClinic } from '~/state/activeSelection';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';
import { useGeolocation } from '~/hooks/useGeolocation';

const styles: StylesConfig<ComboBoxPrediction, IsMulti> = {
  menuList: (base) => ({
    ...base,
    overflow: 'hidden',
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

type LocationInputProps = {
  inputId?: string;
  isDisabled: boolean;
  // NOTE:
  // geolocationProvider exposed for testing purposes only
  geolocationProvider?: Geolocation;
};

export const LOCATION_INPUT_ID = 'location-input';

export function LocationInput({
  inputId,
  isDisabled,
  geolocationProvider,
}: LocationInputProps) {
  const ref = useRef<SelectInstance<ComboBoxPrediction, IsMulti>>(null);
  const location = useAppSelector(selectLocation);
  const hasLocation = useAppSelector(selectHasLocation);
  const canUseGeolocation = useAppSelector(selectCanUseGeolocation);
  const dispatch = useAppDispatch();

  const { loadPredictionsDebounced, error, setError, isLoading } =
    useAutocompleteService();

  const locationFound = useCallback(
    (location: Location) => {
      if (location.isWithinSupportedArea) {
        dispatch(setLocation(location));
        dispatch(clearSelectedClinic());
        if (!location.coordinates) return;
        dispatch(setMapBounds(calculateBoundingBox(location.coordinates, 5)));
        dispatch(setMapInteractionActive(false));
        dispatch(setLocationManuallySelected(true));
      } else {
        dispatch(setLocation());
        dispatch(clearSelectedClinic());
      }
      dispatch(
        setLocationIsWithinSupportedArea(location.isWithinSupportedArea)
      );
    },
    [dispatch]
  );
  const { getPosition, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocation({
      geolocationProvider,
      suppressLocationOnMount: hasLocation,
      isOptimisticGeolocationEnabled: canUseGeolocation,
      onSuccess: (position) => {
        const locationData: Location = {
          value: position.formattedAddress || 'Using your current location',
          isWithinSupportedArea: position.isWithinSupportedArea,
          coordinates: {
            lat: position.latitude,
            lng: position.longitude,
          },
        };
        locationFound(locationData);
      },
      onError: (err) => {
        // Handle geolocation errors appropriately
        logErrorToSentry({
          type: ErrorLevel.Error,
          message: err?.message || 'Geolocation error',
        });
      },
    });

  useEffect(() => {
    dispatch(
      setCanUseGeolocation(isGeolocationAvailable && isGeolocationEnabled)
    );
  }, [dispatch, isGeolocationAvailable, isGeolocationEnabled]);

  //listen for backspaces to clear errors
  // without making the input a controlled component
  useEffect(() => {
    const selectElement = ref.current?.inputRef;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Backspace' && error !== null) {
        setError(null);
      }
    };

    selectElement?.addEventListener('keydown', handleKeyDown);

    return () => {
      selectElement?.removeEventListener('keydown', handleKeyDown);
    };
  }, [error, setError]);

  const handleChange = useCallback(
    (
      selection: ComboBoxPrediction | null,
      { action }: ActionMeta<ComboBoxPrediction | null>
    ) => {
      error && setError(null);

      if (action !== 'select-option') return;

      if (selection?.geocoded) {
        const locationData: Location = {
          coordinates: selection.geocoded.coordinates,
          isWithinSupportedArea: selection.geocoded.isWithinSupportedArea,
          value: selection.isUseCurrentLocation
            ? selection.geocoded.formattedAddress || ''
            : selection.label,
        };

        locationFound(locationData);
        return;
      }

      if (selection?.isUseCurrentLocation) {
        getPosition();
        return;
      }
    },
    [error, setError, getPosition, locationFound]
  );

  const getDefaultValue = () => {
    if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
      return location?.value
        ? {
            label: location?.value,
            value: location?.value,
          }
        : null; // Use null instead of undefined for React Select, undefined does not reset on browser back event
    } else {
      return {
        label: defaultMapLocation.value,
        value: defaultMapLocation.value,
      };
    }
  };

  return (
    <AsyncSelect
      inputId={inputId || LOCATION_INPUT_ID}
      ref={ref}
      isClearable
      blurInputOnSelect
      defaultOptions={[UseCurrentLocationOption]}
      value={getDefaultValue()}
      closeMenuOnSelect
      placeholder="Address, neighbourhood, or city"
      isDisabled={isLoading || isDisabled}
      styles={{ ...inputBaseStyles, ...styles }}
      onChange={handleChange}
      aria-label="Search for your location by address, neighbourhood, or city"
      className="w-full"
      loadOptions={loadPredictionsDebounced}
      maxMenuHeight={600}
      components={{
        Control,
        Option: LocationInputOption,
        LoadingIndicator: () => null,
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null,
        LoadingMessage: () => null,
        ClearIndicator: () => null,
        NoOptionsMessage: (props) =>
          /*This component appears inside the dropdown menu, so using it for error messaging*/
          error ? <NoOptionsMessage {...props} error={error} /> : null,
      }}
    />
  );
}
